import { Box, Button, Flex, Grid, GridItem, Image, Input, InputGroup, InputLeftElement, Text, Spinner, Center, } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import MyDiv from './myDepartment.style';
import { EmployeeModel } from '../../models/employee';
import { Search2Icon } from '@chakra-ui/icons';
import { CustomPagination, CustomToast } from '../../components';
import CardIcon from '../../assets/images/CardIcon.svg';
import ListIcon from '../../assets/images/ListIcon.svg';
import { MyDepartmentCard, MyDepartmentList } from './components';
import { ILazyParams, useFetch } from '../../hooks/useFetch';

interface IEmployeeData {
  employee: EmployeeModel[];
  totalCount: number;
}
interface StatusDD {
  id: number;
  employeeStatus: string;
}

const Employee = () => {
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [employees, setEmployees] = useState<{ data: EmployeeModel[], totalCount: number }>({ data: [], totalCount: 0, });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [Empdepartment, setEmpDepartment] = useState<string>("");
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [statusDD, setStatusDD] = useState<StatusDD[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const { addToast } = CustomToast();
  const { fetchApi, } = useFetch(addToast);
  const [query, setQuery] = useState<{
    lazyParams: ILazyParams,
    filter: Record<string, number[]>,
    search?: string | null
  }>(
    {
      lazyParams: { first: 0, rows: 9, page: 0, sortField: '', sortOrder: 1, },
      filter: {}
    });

  const pageCount = Math.ceil(employees.totalCount / query.lazyParams.rows);

  const handlePageClick = ({ selected }: { selected: number }) => {
    setQuery(prev => {
      const updatedParams = {
        ...prev.lazyParams,
        page: selected,
        first: selected * prev.lazyParams.rows,
      };
      return { ...prev, lazyParams: updatedParams };
    });
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);



  useEffect(() => {
    const loginDataString = sessionStorage.getItem('loginData');
    if (loginDataString) {
      const loginData = JSON.parse(loginDataString);
      setEmployeeId(loginData.employeeId);
    }
  }, []);

  useEffect(() => {
    if (employeeId) {
      getDepartmentId();
    }
  }, [employeeId]);

  const getDepartmentId = async () => {
    if (!employeeId) return;
    const response = await fetchApi<any>("Hrlense_Employee/getEmpDepartment", "GET", null, `id=${employeeId}`, '');
    if (response && response.length > 0) {
      setEmpDepartment(response[0].departmentId);
    }
  };

  useEffect(() => {
    if (!Empdepartment) return;
    loadEmployees();
  }, [Empdepartment, query ,debouncedSearch]);

  const loadEmployees = async () => {
    setLoading(true);
    setError(null);

    try {
      const _query = new URLSearchParams({
        department_Id: Empdepartment,
        lazyParams: JSON.stringify(query.lazyParams),
        search: debouncedSearch ?? ''
      }).toString();

      const result = await fetchApi<IEmployeeData>('Hrlense_Employee', 'GET', null, _query);

      if (result) {
        setEmployees({ data: result.employee, totalCount: result.totalCount });
      } else {
        setError("No data found");
      }
    } catch (err) {
      setError("Failed to load employee data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const getStatusDropdown = async () => {
      try {
        const response = await fetchApi("DropDown/EmplyeeStatus", "GET", null, "", "");
        if (response?.length > 0) {
          const modifiedStatus = response.filter((item: any) =>
            ["Active", "Terminated", "Release", "Abscond"].includes(item.employeeStatus));
          setStatusDD(modifiedStatus);
        } else {
          addToast({ message: "Empty employee status dropdown.", status: "error" })
        }
      } catch (error) {
        console.error("Failed to load employee status", error);
      }
    };

    getStatusDropdown();
  }, []);

  const setRowsPerPage = (rows: number) => {
    setQuery(prev => ({
      ...prev,
      lazyParams: {
        ...prev.lazyParams,
        rows,
        page: 0,
        first: 0
      }
    }));
  };


  return (
    <MyDiv>
      <Box className="page_wrapper">
        <Flex className="page_heading flex_header">
          <Text className="font-poppins text_3xl text_semibold">My Department</Text>
          <Flex gap={2} align="center">
            <InputGroup width="357px" height="37px">
              <InputLeftElement pointerEvents="none">
                <Search2Icon color="rgba(102, 112, 133, 1)" />
              </InputLeftElement>
              <Input
                type="text"
                className="font-poppins text_regular text_md"
                placeholder="Search employee name"
                maxW="400px"
                onChange={(e) => { handleSearch(e.target.value) }}
              />
            </InputGroup>
            <Button
              className='listView'
              bg={viewMode === 'list' ? 'rgba(234, 236, 240, 1)' : 'transparent'}
              _hover={{ bg: 'rgba(234, 236, 240, 0.8)' }}
              width="44px"
              height="43px"
              padding="0"
              onClick={() => setViewMode('list')}
            >
              <Image src={ListIcon} alt="card" w="26px" h="26px" margin="auto" />
            </Button>
            <Button
              className='listView'
              bg={viewMode === 'card' ? 'rgba(234, 236, 240, 1)' : 'transparent'}
              _hover={{ bg: 'rgba(234, 236, 240, 0.8)' }}
              width="44px"
              height="43px"
              padding="0"
              onClick={() => setViewMode('card')}
            >
              <Image src={CardIcon} alt="Card" w="22px" h="22px" margin="auto" />
            </Button>
          </Flex>
        </Flex>

        <Box position="relative" minH="400px">
          {loading ? (
            <Center position="absolute" top={0} left={0} right={0} bottom={0}>
              <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="#006838" size="xl" />
            </Center>

          ) : error ? (
            <Text color="red.500" mt={4} textAlign="center">
              {error}
            </Text>
          ) : viewMode === 'card' ? (
            <Grid className="grid_container" rowGap={4} columnGap={4}>
              {employees.data?.map((emp) => (
                <GridItem colSpan={4} key={emp.id}>
                  <MyDepartmentCard employee={emp} statusDD={statusDD} />
                </GridItem>
              ))}
            </Grid>
          ) : (
            <Grid className="grid_container">
              <GridItem colSpan={12}>
                <MyDepartmentList
                  employees={employees.data}
                  statusDD={statusDD}
                />
              </GridItem>
            </Grid>
          )}
        </Box>
        <CustomPagination pageCount={pageCount} rowsPerPageOptions={[9, 30, 90, 210]} handlePageClick={handlePageClick} rowsPerPage={query.lazyParams.rows} setRowsPerPage={setRowsPerPage} totalRecords={employees.totalCount} currentPage={query.lazyParams.page} />
      </Box>
    </MyDiv>
  );
};

export default Employee;
